import React, { useEffect, useMemo, useState, useCallback } from "react";
import mqtt from "mqtt";
import {
  Truck,
  Package,
  Route,
  CheckCircle2,
  AlertTriangle,
  Clock,
  MapPin,
  ChevronRight,
  ArrowUpRight,
  Zap,
  X,
  SlidersHorizontal,
} from "lucide-react";
import MapView from "./MapView";
import PageHeader from "./ui/PageHeader";
import KpiCard from "./ui/KpiCard";
import Badge from "./ui/Badge";
import ProgressBar from "./ui/ProgressBar";
import Card from "./ui/Card";
import { apiFetch } from "../lib/apiClient";
import { getClientes } from "../lib/clientesService";
import { geocodeAddresses } from "../lib/mapGeocoding";
import {
  buildMapRoutes,
  buildVehicleMarkers,
  countMapStats,
  isActiveRoute,
  routeProgress,
} from "../lib/mapRouteData";
import { isUrgentAlerta } from "../lib/alertasConductorUtils";

const DASHBOARD_ESTADO_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "PENDIENTE", label: "Pendiente" },
  { value: "ASIGNADO", label: "Asignado" },
  { value: "EN_CAMINO_ORIGEN", label: "En camino origen" },
  { value: "EN_CARGA", label: "En carga" },
  { value: "EN_TRANSITO", label: "En tránsito" },
  { value: "EN_DESTINO", label: "En destino" },
  { value: "ENTREGADO", label: "Entregado" },
  { value: "CANCELADO", label: "Cancelado" },
];

const EMPTY_FILTERS = { clienteId: "", estado: "", desde: "", hasta: "" };

const KPI_CONFIG = [
  { key: "rutasActivas", label: "Rutas activas", icon: Truck, iconClass: "lt-kpi-icon--blue", isPercent: false },
  { key: "rutasCompletadas", label: "Completadas", icon: CheckCircle2, iconClass: "lt-kpi-icon--green", isPercent: false, trend: "up" },
  { key: "rutasPendientes", label: "Pendientes", icon: Clock, iconClass: "lt-kpi-icon--amber", isPercent: false },
  { key: "rutasConAlertas", label: "Con alertas", icon: AlertTriangle, iconClass: "lt-kpi-icon--red", isPercent: false },
  { key: "sla", label: "SLA entregas", icon: Package, iconClass: "lt-kpi-icon--purple", isPercent: true, trend: "up" },
];

function mensajeConductorToMapAlert(mensaje, rutasById = {}) {
  const ruta = rutasById[mensaje.ruta_id];
  const lat = mensaje.latitud != null ? Number(mensaje.latitud) : null;
  const lng = mensaje.longitud != null ? Number(mensaje.longitud) : null;
  return {
    id: mensaje.id,
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
    priority: "ALTA",
    vehicle_plate: ruta?.camiones?.patente ?? "—",
    driver_name: ruta?.conductores?.rut ?? "—",
    alert_type: mensaje.mensaje === "Emergencia" ? "BOTON_PANICO" : mensaje.mensaje,
    status: "ACTIVA",
  };
}

function buildResumenUrl(filters) {
  const params = new URLSearchParams();
  if (filters.clienteId?.trim()) params.set("clienteId", filters.clienteId.trim());
  if (filters.estado?.trim()) params.set("estado", filters.estado.trim());
  if (filters.desde?.trim()) params.set("desde", filters.desde.trim());
  if (filters.hasta?.trim()) params.set("hasta", filters.hasta.trim());
  const qs = params.toString();
  return qs ? `/api/dashboard/resumen?${qs}` : "/api/dashboard/resumen";
}

function buildRutasUrl(filters) {
  const params = new URLSearchParams();
  params.set("limit", "500");
  if (filters.clienteId?.trim()) params.set("clienteId", filters.clienteId.trim());
  if (filters.estado?.trim()) params.set("estado", filters.estado.trim());
  const qs = params.toString();
  return `/api/rutas?${qs}`;
}

function matchesDateRange(createdAt, desde, hasta) {
  if (!desde?.trim() && !hasta?.trim()) return true;
  if (!createdAt) return false;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return false;
  if (desde?.trim()) {
    const from = new Date(`${desde.trim()}T00:00:00`);
    if (date < from) return false;
  }
  if (hasta?.trim()) {
    const to = new Date(`${hasta.trim()}T23:59:59.999`);
    if (date > to) return false;
  }
  return true;
}

function formatKpiValue(key, value, isPercent) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  if (isPercent) {
    const n = Number(value);
    return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
  }
  return String(value);
}

function formatHeaderDate() {
  return new Date().toLocaleDateString("es-CL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function fmtEtaDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function fmtEtaTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function routeBadgeVariant(estado) {
  const e = String(estado || "").toUpperCase();
  if (["ENTREGADO", "COMPLETADO"].includes(e)) return "success";
  if (["CANCELADO", "CANCELADA"].includes(e)) return "danger";
  if (["EN_TRANSITO", "EN_DESTINO", "ASIGNADO", "EN_CARGA", "EN_CAMINO_ORIGEN"].includes(e)) return "accent";
  if (e === "PENDIENTE") return "warning";
  return "muted";
}

function routeStatusLabel(estado) {
  if (!estado) return "Sin estado";
  return String(estado).replace(/_/g, " ");
}

const ONLINE_THRESHOLD_MIN = 5;

function getOnlineStatus(tracking) {
  if (!tracking) return { label: "Sin señal", color: "#9ca3af" };
  const ubic = tracking.ubicacion_actual;
  if (!ubic?.timestamp_evento) return { label: "Sin señal", color: "#9ca3af" };
  const ageMin = (Date.now() - Date.parse(String(ubic.timestamp_evento))) / 60000;
  if (!Number.isFinite(ageMin)) return { label: "Sin señal", color: "#9ca3af" };
  if (ageMin <= ONLINE_THRESHOLD_MIN) return { label: "Online", color: "#16a34a" };
  return { label: "Offline", color: "#ef4444" };
}

function resolveTrackingForRoute(routeId, routeTracking, eventosConductor) {
  const apiTracking = routeTracking[routeId];
  const withGps = eventosConductor
    .filter(
      (m) =>
        m.ruta_id === routeId &&
        m.latitud != null &&
        m.longitud != null &&
        m.timestamp_evento &&
        Number.isFinite(Number(m.latitud)) &&
        Number.isFinite(Number(m.longitud)),
    )
    .sort((a, b) => new Date(b.timestamp_evento) - new Date(a.timestamp_evento));
  const latestEvento = withGps[0];
  if (!latestEvento) return apiTracking ?? null;

  const eventoTracking = {
    ubicacion_actual: {
      latitud: Number(latestEvento.latitud),
      longitud: Number(latestEvento.longitud),
      timestamp_evento: latestEvento.timestamp_evento,
    },
    historial: apiTracking?.historial ?? [],
  };

  if (!apiTracking?.ubicacion_actual?.timestamp_evento) return eventoTracking;

  const apiTs = Date.parse(String(apiTracking.ubicacion_actual.timestamp_evento));
  const evtTs = Date.parse(String(latestEvento.timestamp_evento));
  if (Number.isFinite(apiTs) && Number.isFinite(evtTs) && apiTs >= evtTs) return apiTracking;
  return eventoTracking;
}

function routeIdFromMqttTopic(topic) {
  const parts = String(topic || "").split("/");
  return parts.length >= 3 ? parts[2] : null;
}

function OperationalKpiSkeleton({ label }) {
  return (
    <div className="lt-kpi-card lt-kpi-card--skeleton" aria-hidden="true">
      <div className="lt-kpi-card__icon lt-kpi-icon--blue" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="lt-kpi-card__label">{label || "···"}</div>
        <div className="lt-kpi-card__value">—</div>
      </div>
    </div>
  );
}

/**
 * Dashboard operacional con mapa en vivo.
 * Alertas operativas: única fuente mensajes_conductor (isUrgentAlerta), alineado con Centro de Alertas.
 */
export default function DashboardOperational({
  eventosConductor = [],
  mensajes,
  urgentCount = 0,
  operator,
  onNavigate,
  isDark = false,
}) {
  const conductorEventos = mensajes ?? eventosConductor;
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [clientesLoading, setClientesLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);
  const [rutas, setRutas] = useState([]);
  const [rutasLoading, setRutasLoading] = useState(true);
  const [camiones, setCamiones] = useState([]);
  const [, setCamionesLoading] = useState(true);
  const [geocodeMap, setGeocodeMap] = useState({});
  const [geocoding, setGeocoding] = useState(false);
  const [mapFilter, setMapFilter] = useState("todos");
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [routeTracking, setRouteTracking] = useState({});
  const [, setRouteTrackingLoading] = useState(false);
  const [onlineTick, setOnlineTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setClientesLoading(true);
      try {
        const data = await getClientes();
        if (!cancelled) setClientes(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setClientes([]);
      } finally {
        if (!cancelled) setClientesLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const loadDashboardResumen = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const result = await apiFetch(buildResumenUrl(filters));
      if (!result.ok) {
        throw new Error(result.error || result.data?.message || `HTTP ${result.status}`);
      }
      setDashboardData(result.data ?? null);
    } catch (err) {
      setDashboardData(null);
      setDashboardError(err?.message || "Error al cargar el resumen.");
    } finally {
      setDashboardLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDashboardResumen();
  }, [loadDashboardResumen]);

  const loadRutas = useCallback(async () => {
    setRutasLoading(true);
    try {
      const result = await apiFetch(buildRutasUrl(filters));
      if (!result.ok) { setRutas([]); return; }
      const payload = result.data;
      setRutas(Array.isArray(payload) ? payload : payload?.data ?? []);
    } catch {
      setRutas([]);
    } finally {
      setRutasLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadRutas();
  }, [loadRutas]);

  useEffect(() => {
    let cancelled = false;
    async function fetchCamiones() {
      setCamionesLoading(true);
      try {
        let result = await apiFetch("/api/camiones/disponibles");
        if (!result.ok) result = await apiFetch("/api/camiones");
        if (cancelled) return;
        if (!result.ok) { setCamiones([]); return; }
        const payload = result.data;
        setCamiones(Array.isArray(payload) ? payload : payload?.data ?? []);
      } catch {
        if (!cancelled) setCamiones([]);
      } finally {
        if (!cancelled) setCamionesLoading(false);
      }
    }
    fetchCamiones();
    return () => { cancelled = true; };
  }, []);

  const filteredRutas = useMemo(
    () => rutas.filter((r) => matchesDateRange(r.created_at, filters.desde, filters.hasta)),
    [rutas, filters.desde, filters.hasta],
  );

  const activeRoutesRaw = useMemo(() => filteredRutas.filter(isActiveRoute), [filteredRutas]);

  const activeRouteIds = useMemo(
    () => activeRoutesRaw.map((r) => r.id).filter(Boolean),
    [activeRoutesRaw],
  );

  const activeRouteIdsKey = activeRouteIds.join(",");

  useEffect(() => {
    const timer = setInterval(() => setOnlineTick((t) => t + 1), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const addresses = activeRoutesRaw.flatMap((r) => [r.origen, r.destino].filter(Boolean));
    if (addresses.length === 0) return undefined;

    async function geocode() {
      setGeocoding(true);
      try {
        const map = await geocodeAddresses(addresses);
        if (!cancelled) setGeocodeMap((prev) => ({ ...prev, ...map }));
      } catch {
        /* geocoding opcional — rutas sin coords muestran solo marcador si hay GPS */
      } finally {
        if (!cancelled) setGeocoding(false);
      }
    }
    geocode();
    return () => { cancelled = true; };
  }, [activeRoutesRaw]);

  const rutasById = useMemo(
    () =>
      rutas.reduce((acc, ruta) => {
        if (ruta?.id) acc[ruta.id] = ruta;
        return acc;
      }, {}),
    [rutas],
  );

  const urgentAlertas = useMemo(
    () => conductorEventos.filter(isUrgentAlerta),
    [conductorEventos],
  );

  const mapAlerts = useMemo(() => {
    if (mapFilter === "completados") return [];
    return urgentAlertas
      .map((m) => mensajeConductorToMapAlert(m, rutasById))
      .filter((a) => a.lat != null && a.lng != null);
  }, [urgentAlertas, rutasById, mapFilter]);

  const mapRoutes = useMemo(
    () => buildMapRoutes(activeRoutesRaw, geocodeMap, conductorEventos),
    [activeRoutesRaw, geocodeMap, conductorEventos],
  );

  const vehicleMarkers = useMemo(
    () => buildVehicleMarkers(camiones, mapRoutes),
    [camiones, mapRoutes],
  );

  const mapStats = useMemo(
    () => countMapStats(mapRoutes, camiones, vehicleMarkers, conductorEventos),
    [mapRoutes, camiones, vehicleMarkers, conductorEventos],
  );

  const selectedRoute = useMemo(
    () => mapRoutes.find((r) => r.id === selectedRouteId) ?? null,
    [mapRoutes, selectedRouteId],
  );

  const selectedRuta = useMemo(
    () => rutas.find((r) => r.id === selectedRouteId) ?? null,
    [rutas, selectedRouteId],
  );

  const selectedRouteConductorLabel = useMemo(() => {
    if (!selectedRuta) return selectedRoute?.conductor || "—";

    const conductorNombre =
      selectedRuta.conductores?.usuarios?.nombre ||
      selectedRuta.conductores?.nombre ||
      selectedRuta.conductor_nombre ||
      "";
    const conductorRut =
      selectedRuta.conductores?.rut ||
      selectedRuta.conductor_rut ||
      selectedRoute?.conductor ||
      "";

    if (conductorNombre && conductorRut) return `${conductorNombre} - ${conductorRut}`;
    return conductorNombre || conductorRut || "—";
  }, [selectedRuta, selectedRoute]);

  const focusedRoute = useMemo(() => {
    if (!selectedRoute) return null;
    const tracking = routeTracking[selectedRouteId];
    if (!tracking) return selectedRoute;

    const ubic = tracking.ubicacion_actual;
    const vehicleGps = ubic && ubic.latitud != null && ubic.longitud != null
      ? { lat: Number(ubic.latitud), lng: Number(ubic.longitud), timestamp_evento: ubic.timestamp_evento }
      : selectedRoute.vehicleGps || null;

    return { ...selectedRoute, vehicleGps, tracking };
  }, [selectedRoute, routeTracking, selectedRouteId]);

  /** Solo la ruta seleccionada se dibuja en el mapa (visor individual). */
  const visibleMapRoutes = useMemo(
    () => (selectedRoute ? [selectedRoute] : []),
    [selectedRoute],
  );

  const visibleVehicleMarkers = useMemo(
    () =>
      selectedRouteId
        ? vehicleMarkers.filter((v) => v.routeId === selectedRouteId)
        : [],
    [vehicleMarkers, selectedRouteId],
  );

  const onlineStatusesByRoute = useMemo(() => {
    void onlineTick;
    const statuses = {};
    activeRoutesRaw.forEach((ruta) => {
      const tracking = resolveTrackingForRoute(ruta.id, routeTracking, conductorEventos);
      statuses[ruta.id] = getOnlineStatus(tracking);
    });
    return statuses;
  }, [activeRoutesRaw, routeTracking, conductorEventos, onlineTick]);

  const hasActiveFilters = Boolean(filters.clienteId || filters.estado || filters.desde || filters.hasta);

  const handleRouteSelect = (routeId) => {
    setSelectedRouteId((prev) => (prev === routeId ? null : routeId));
  };

  useEffect(() => {
    if (!activeRouteIdsKey) return undefined;
    let cancelled = false;

    (async () => {
      setRouteTrackingLoading(true);
      try {
        const results = await Promise.allSettled(
          activeRouteIds.map(async (routeId) => {
            const res = await apiFetch(`/api/rutas/${routeId}/tracking`);
            return {
              routeId,
              data: res.ok && res.data
                ? res.data
                : { ubicacion_actual: null, historial: [] },
            };
          }),
        );
        if (cancelled) return;
        setRouteTracking((prev) => {
          const next = { ...prev };
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              next[result.value.routeId] = result.value.data;
            }
          });
          return next;
        });
      } finally {
        if (!cancelled) setRouteTrackingLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [activeRouteIds, activeRouteIdsKey]);

  useEffect(() => {
    if (!activeRouteIdsKey) return undefined;
    let mounted = true;

    const topics = activeRouteIds.flatMap((routeId) => [
      `logitrack/rutas/${routeId}/gps`,
      `logitrack/rutas/${routeId}/estado`,
    ]);

    const brokerUrl = process.env.REACT_APP_MQTT_BROKER_URL || "ws://localhost:9001";
    const client = mqtt.connect(brokerUrl, { reconnectPeriod: 5000 });

    const safeJson = (buf) => {
      try {
        return JSON.parse(Buffer.isBuffer(buf) ? buf.toString() : String(buf));
      } catch {
        try { return JSON.parse(String(buf)); } catch { return null; }
      }
    };

    client.on("connect", () => {
      client.subscribe(topics, (err) => {
        if (err) console.error("MQTT subscribe error", err);
      });
    });

    client.on("error", (err) => {
      console.error("MQTT error", err);
    });

    client.on("message", (topic, message) => {
      if (!mounted) return;
      const routeId = routeIdFromMqttTopic(topic);
      if (!routeId) return;

      const payload = safeJson(message);
      if (!payload) return;

      if (topic.endsWith("/gps")) {
        const lat = payload.lat ?? payload.latitude ?? payload.latitud;
        const lng = payload.lng ?? payload.longitude ?? payload.longitud;
        const timestamp = payload.timestamp ?? payload.timestamp_evento ?? new Date().toISOString();
        const ubic = lat != null && lng != null
          ? { latitud: Number(lat), longitud: Number(lng), timestamp_evento: timestamp }
          : null;
        if (ubic) {
          setRouteTracking((prev) => {
            const prevItem = prev[routeId] || { ubicacion_actual: null, historial: [] };
            const newHist = Array.isArray(prevItem.historial) ? prevItem.historial.concat([ubic]) : [ubic];
            return { ...prev, [routeId]: { ...prevItem, ubicacion_actual: ubic, historial: newHist } };
          });
        }
      } else if (topic.endsWith("/estado")) {
        const estado = payload.estado ?? payload.status ?? payload;
        if (estado) {
          setRutas((prev) => prev.map((r) => (r.id === routeId ? { ...r, estado } : r)));
        }
      }
    });

    return () => {
      mounted = false;
      try {
        client.unsubscribe(topics, () => { /* noop */ });
      } catch (e) {
        /* ignore */
      }
      try {
        client.end(true);
      } catch (e) {
        /* ignore */
      }
    };
  }, [activeRouteIds, activeRouteIdsKey]);

  const kpiValues = KPI_CONFIG.map((cfg) => ({
    ...cfg,
    value: formatKpiValue(cfg.key, dashboardData?.[cfg.key], cfg.isPercent),
    sub: cfg.isPercent && dashboardData?.[cfg.key] != null ? "Cumplimiento SLA" : undefined,
  }));

  const mapOverlay = (
    <>
      <div className="lt-map-overlay lt-map-overlay--top-left">
        <div className="lt-map-live-pill">
          En mapa · {mapStats.vehiclesOnMap} vehículos
        </div>
      </div>

      <div className="lt-map-overlay lt-map-overlay--top-right">
        <div className="lt-map-stat-card">
          <div className="lt-map-stat-card__label">Vehículos activos</div>
          <div className="lt-map-stat-card__value">{mapStats.vehiclesOnMap}</div>
        </div>
        <div className="lt-map-stat-card">
          <div className="lt-map-stat-card__label">Rutas en tránsito</div>
          <div className="lt-map-stat-card__value" style={{ color: "var(--lt-accent)" }}>
            {mapStats.inTransit}
          </div>
        </div>
        <div className="lt-map-stat-card">
          <div className="lt-map-stat-card__label">Alertas abiertas</div>
          <div className="lt-map-stat-card__value" style={{ color: "var(--lt-danger)" }}>
            {urgentCount}
          </div>
        </div>
      </div>

      {!selectedRouteId && (
        <div className="lt-map-overlay lt-map-overlay--center">
          <p className="lt-map-select-hint">
            Selecciona una ruta para visualizarla en el mapa
          </p>
        </div>
      )}

      {selectedRoute && selectedRuta && (
        <div className="lt-map-route-panel">
          <button
            type="button"
            className="lt-map-route-panel__close"
            onClick={() => setSelectedRouteId(null)}
            aria-label="Cerrar panel"
          >
            <X size={14} />
          </button>
          <div className="lt-map-route-panel__header">
            <span className="lt-map-route-panel__id">
              {selectedRuta.nombre_ruta || (selectedRuta.origen && selectedRuta.destino ? `${selectedRuta.origen.split(',')[0]} - ${selectedRuta.destino.split(',')[0]}` : `Ruta #${String(selectedRuta.id).substring(0, 6).toUpperCase()}`)}
            </span>
            <Badge variant={routeBadgeVariant(selectedRoute.estado)}>
              {routeStatusLabel(selectedRoute.estado)}
            </Badge>
            {selectedRoute.hasAlert && <Badge variant="danger">ALERTA</Badge>}
          </div>
          <div className="lt-map-route-panel__grid">
            <div>
              <div className="lt-map-route-panel__label">Vehículo</div>
              <div className="lt-map-route-panel__value">{selectedRoute.patente || "—"}</div>
            </div>
            <div>
              <div className="lt-map-route-panel__label">Conductor</div>
              <div className="lt-map-route-panel__value">{selectedRouteConductorLabel}</div>
            </div>
            <div>
              <div className="lt-map-route-panel__label">Estado</div>
              <div className="lt-map-route-panel__value">{routeStatusLabel(selectedRoute.estado)}</div>
            </div>
          </div>
          <div className="lt-map-route-panel__progress">
            <div className="lt-map-route-panel__progress-header">
              <span>Progreso</span>
              <strong>{selectedRoute.progress}%</strong>
            </div>
            <ProgressBar
              value={selectedRoute.progress}
              color={selectedRoute.hasAlert ? "var(--lt-danger)" : "var(--lt-accent)"}
            />
          </div>
          <div className="lt-map-route-panel__route">
            <MapPin size={11} /> {selectedRoute.origen || "—"}
            <ArrowUpRight size={11} style={{ margin: "0 4px" }} />
            {selectedRoute.destino || "—"}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="lt-dashboard lt-dashboard--map-first">
      <div className="lt-dashboard__top-bar">
        <PageHeader
          title="Operaciones"
          subtitle={`${formatHeaderDate()} · ${operator?.branch ?? "Todas las sucursales"}`}
          actions={
            <>
              <button
                type="button"
                className={`lt-btn lt-btn--secondary ${showFilters ? "lt-btn--filter-active" : ""}`}
                onClick={() => setShowFilters((v) => !v)}
              >
                <SlidersHorizontal size={14} />
                Filtros
              </button>
              <button
                type="button"
                className="lt-btn lt-btn--primary"
                onClick={() => onNavigate?.("rutas")}
              >
                <Route size={14} />
                Pedidos
              </button>
            </>
          }
        />

        {showFilters && (
          <Card className="lt-dashboard__filters-pop">
            <div className="lt-filters-bar lt-filters-bar--compact">
              <div>
                <label className="lt-label" htmlFor="dash-filter-cliente">Cliente</label>
                <select
                  id="dash-filter-cliente"
                  className="lt-select"
                  value={filters.clienteId}
                  onChange={(e) => setFilters((p) => ({ ...p, clienteId: e.target.value }))}
                  disabled={clientesLoading}
                >
                  <option value="">{clientesLoading ? "Cargando..." : "Todos"}</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre || c.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="lt-label" htmlFor="dash-filter-estado">Estado</label>
                <select
                  id="dash-filter-estado"
                  className="lt-select"
                  value={filters.estado}
                  onChange={(e) => setFilters((p) => ({ ...p, estado: e.target.value }))}
                >
                  {DASHBOARD_ESTADO_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="lt-label" htmlFor="dash-filter-desde">Desde</label>
                <input id="dash-filter-desde" type="date" className="lt-input" value={filters.desde}
                  onChange={(e) => setFilters((p) => ({ ...p, desde: e.target.value }))} />
              </div>
              <div>
                <label className="lt-label" htmlFor="dash-filter-hasta">Hasta</label>
                <input id="dash-filter-hasta" type="date" className="lt-input" value={filters.hasta}
                  onChange={(e) => setFilters((p) => ({ ...p, hasta: e.target.value }))} />
              </div>
              <div style={{ alignSelf: "end" }}>
                <button type="button" className="lt-btn lt-btn--secondary" disabled={!hasActiveFilters}
                  onClick={() => setFilters(EMPTY_FILTERS)}>Limpiar</button>
              </div>
            </div>
          </Card>
        )}

        {dashboardError && (
          <div className="lt-error-banner lt-error-banner--compact" role="alert">
            <span>{dashboardError}</span>
            <button
              type="button"
              className="lt-btn lt-btn--secondary"
              style={{ marginLeft: "12px" }}
              onClick={loadDashboardResumen}
            >
              Reintentar
            </button>
          </div>
        )}

        <div className="lt-kpi-strip lt-kpi-strip--compact" aria-busy={dashboardLoading}>
          {dashboardLoading
            ? KPI_CONFIG.map((kpi) => (
                <OperationalKpiSkeleton key={kpi.key} label={kpi.label} />
              ))
            : dashboardData
              ? kpiValues.map((kpi) => (
                  <KpiCard
                    key={kpi.key}
                    icon={kpi.icon}
                    label={kpi.label}
                    value={kpi.value}
                    sub={kpi.sub}
                    iconClass={kpi.iconClass}
                    trend={kpi.trend}
                  />
                ))
              : null}
        </div>
      </div>

      <div className="lt-dashboard__map-hero">
        <div className="lt-dashboard__map-wrap">
          <div className="lt-dashboard__map-toolbar">
            <span className="lt-dashboard__map-title">Mapa de operaciones</span>
            <div className="lt-dashboard__map-filters">
              {[
                { key: "todos", label: "Todos" },
                { key: "alertas", label: "Alertas" },
                { key: "completados", label: "Completados" },
              ].map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`lt-btn--filter ${mapFilter === f.key ? "lt-btn--filter-active" : ""}`}
                  onClick={() => setMapFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {geocoding && <span className="lt-dashboard__geocode-hint">Geocodificando rutas…</span>}
          </div>
          <MapView
            alerts={mapAlerts}
            mapRoutes={visibleMapRoutes}
            vehicleMarkers={visibleVehicleMarkers}
            selectedRouteId={selectedRouteId}
            focusedRoute={focusedRoute}
            isDark={isDark}
            onRouteSelect={handleRouteSelect}
            overlay={mapOverlay}
          />
        </div>

        <aside className="lt-dashboard__side-panel">
          <div className="lt-dashboard__side-header">
            <span>Entregas activas</span>
            <span className="lt-tab__count">{activeRoutesRaw.length}</span>
          </div>
          <div className="lt-dashboard__side-scroll lt-scroll">
            {rutasLoading ? (
              <div className="lt-empty">Cargando rutas...</div>
            ) : activeRoutesRaw.length === 0 ? (
              <div className="lt-empty">No hay rutas activas.</div>
            ) : (
              activeRoutesRaw.map((ruta) => {
                const mapRoute = mapRoutes.find((mr) => mr.id === ruta.id);
                const progress = mapRoute?.progress ?? routeProgress(ruta.estado);
                const hasAlert = mapRoute?.hasAlert;
                const onlineStatus = onlineStatusesByRoute[ruta.id] ?? { label: "Sin señal", color: "#9ca3af" };
                return (
                  <div
                    key={ruta.id}
                    className={`lt-list-item lt-list-item--compact ${selectedRouteId === ruta.id ? "lt-list-item--selected" : ""}`}
                    onClick={() => handleRouteSelect(ruta.id)}
                  >
                    <div className="lt-list-item__row">
                      <div>
                        <div className="lt-list-item__title">
                          <span>{ruta.nombre_ruta || (ruta.origen && ruta.destino ? `${ruta.origen.split(',')[0]} - ${ruta.destino.split(',')[0]}` : `Ruta #${String(ruta.id).substring(0, 6).toUpperCase()}`)}</span>
                          <Badge variant={routeBadgeVariant(ruta.estado)}>{routeStatusLabel(ruta.estado)}</Badge>
                          {hasAlert && <Badge variant="danger">!</Badge>}
                        </div>
                        <div className="lt-list-item__sub">
                          <MapPin size={10} /> {ruta.origen || "—"} → {ruta.destino || "—"}
                          <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 10, background: onlineStatus.color, display: "inline-block" }} />
                            <small className="lt-text-muted">{onlineStatus.label}</small>
                          </span>
                        </div>
                      </div>
                      <div className="lt-list-item__eta">
                        <div>{fmtEtaDate(ruta.eta)}</div>
                        <div>{fmtEtaTime(ruta.eta)}</div>
                      </div>
                    </div>
                    <ProgressBar value={progress} color={hasAlert ? "var(--lt-danger)" : "var(--lt-accent)"} />
                  </div>
                );
              })
            )}
          </div>
          <div className="lt-dashboard__side-footer">
            {urgentCount > 0 && (
              <button type="button" className="lt-btn lt-btn--secondary lt-btn--full"
                onClick={() => onNavigate?.("alertas")}>
                <AlertTriangle size={13} />
                Alertas ({urgentCount})
              </button>
            )}
            <button type="button" className="lt-btn lt-btn--ghost lt-btn--full"
              onClick={() => onNavigate?.("rutas")}>
              Ver todas <ChevronRight size={13} />
            </button>
          </div>
        </aside>
      </div>

      {urgentCount > 0 && (
        <div className="lt-dashboard__alert-ticker">
          {urgentAlertas.slice(0, 3).map((mensaje) => {
            const ruta = rutasById[mensaje.ruta_id];
            const patente = ruta?.camiones?.patente ?? "—";
            return (
              <button
                key={mensaje.id}
                type="button"
                className="lt-dashboard__alert-chip"
                onClick={() => onNavigate?.("alertas")}
              >
                <Zap size={11} />
                <span>{patente}</span>
                <Badge variant="danger" showDot={false}>ALTA</Badge>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
